import { useState } from 'react';
import {
  useTranslate,
  useNotification,
  useInvalidate,
  useCustomMutation,
  useMany,
} from '@refinedev/core';
import type { CrudFilter } from '@refinedev/core';
import { List, EditButton, DeleteButton } from '@refinedev/antd';
import { Table, Space, Input, Form, Row, Col, Image, Button } from 'antd';
import { SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { extractBackendMessage } from '@/utils/apiError';
import {
  ModStatus,
  type PublisherBulkImportOut,
  type PublisherListOut,
} from '@/api/generated/schemas';
import { ModStatusTag } from '@/components/common/ModStatusTag';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { PublisherSelectFilter } from '@/components/user_catalog/publishers/PublisherSelectFilter';
import { orDash } from '@/components/common/show/shared';
import { BulkModStatusBar } from '@/components/user_catalog/BulkModStatusBar';
import { useBulkRowSelection } from '@/hooks/user_catalog/useBulkRowSelection';
import { useTableSearch } from '@/hooks/useTableSearch';
import { useTableRowNavigateToShow } from '@/hooks/useTableRowNavigateToShow';
import { useAdminTable } from '@/hooks/useAdminTable';
import { SelectFilter } from '@/components/common/SelectFilter';
import { NumberRangeFilter } from '@/components/common/NumberRangeFilter';
import { DateRangeFilter } from '@/components/common/DateRangeFilter';
import { isFieldFiltered } from '@/utils/filters';
import { getSortOrder } from '@/utils/sorters';

export const PublisherList = () => {
  const t = useTranslate();
  const rowNav = useTableRowNavigateToShow();
  const { onSearch, createDebouncedSubmit } = useTableSearch();
  const { open: notify } = useNotification();
  const invalidate = useInvalidate();
  const { mutateAsync } = useCustomMutation<PublisherBulkImportOut>();
  const [syncing, setSyncing] = useState(false);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data: summary } = await mutateAsync({
        url: 'user_catalog/publishers/import',
        method: 'post',
        values: {},
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: t('user_catalog.publishers.synchronize.bulk.summary', {
          created: summary.created,
          skipped: summary.skipped,
        }),
      });
      invalidate({
        resource: 'user_catalog.publishers',
        invalidates: ['list'],
      });
    } catch (error) {
      const description =
        extractBackendMessage(error) ??
        t('user_catalog.publishers.synchronize.bulk.error_fallback');
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description,
      });
    } finally {
      setSyncing(false);
    }
  };

  const { tableProps, searchFormProps, setFilters, filters, sorters } =
    useAdminTable<PublisherListOut>({
      pagination: { pageSize: 20 },
      resource: 'user_catalog.publishers',
      syncWithLocation: true,
      onSearch,
      sorters: {
        initial: [{ field: 'updated_at', order: 'desc' }],
      },
    });

  const debouncedSubmit = createDebouncedSubmit(searchFormProps.form);
  const selection = useBulkRowSelection<PublisherListOut>();

  const rows = tableProps.dataSource ?? [];
  const parentIds = [
    ...new Set(
      rows.map((r) => r.parent_id).filter((id): id is number => id != null)
    ),
  ];
  const { result: parentsResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: parentIds,
    queryOptions: { enabled: parentIds.length > 0 },
  });
  const parentMap = new Map<number, PublisherListOut>(
    (parentsResult?.data ?? []).map((p) => [p.id, p])
  );

  return (
    <List
      headerButtons={({ defaultButtons }) => (
        <>
          {defaultButtons}
          <Button
            type="primary"
            icon={<SyncOutlined spin={syncing} />}
            onClick={handleSync}
            loading={syncing}
          >
            {t('user_catalog.publishers.synchronize.bulk.button')}
          </Button>
        </>
      )}
    >
      <Form {...searchFormProps} layout="vertical">
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={16} md={12} lg={8}>
            <Form.Item name="q" style={{ marginBottom: 0 }}>
              <Input
                placeholder={t('user_catalog.publishers.search.placeholder')}
                prefix={<SearchOutlined />}
                allowClear
                onChange={(e) => debouncedSubmit(e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <BulkModStatusBar
        resource="user_catalog.publishers"
        selection={selection}
      />

      <Table
        {...rowNav('user_catalog.publishers', tableProps)}
        rowKey="id"
        rowSelection={selection.rowSelection}
      >
        <Table.Column
          dataIndex="id"
          title={t('base.fields.id')}
          sorter
          sortOrder={getSortOrder(sorters, 'id')}
        />

        <Table.Column
          dataIndex="cover_url"
          title={t('user_catalog.publishers.fields.cover_url')}
          render={(value: string) =>
            value ? (
              <Image
                src={value}
                height={60}
                style={{ objectFit: 'cover' }}
                preview={false}
              />
            ) : (
              '-'
            )
          }
        />

        <Table.Column
          dataIndex="name"
          title={t('user_catalog.publishers.fields.name')}
        />

        <Table.Column<PublisherListOut>
          dataIndex="parent_id"
          title={t('user_catalog.publishers.fields.parent')}
          render={(value: number | null) =>
            orDash(
              value != null && (
                <PublisherIconLink
                  publisherId={value}
                  publisher={parentMap.get(value)}
                />
              )
            )
          }
          filtered={isFieldFiltered(filters as CrudFilter[], 'parent_id')}
          filterDropdown={({ confirm, clearFilters }) => (
            <PublisherSelectFilter
              field="parent_id"
              only="parents"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="mod_status"
          title={t('base.fields.mod_status')}
          render={(value: string) => <ModStatusTag value={value} />}
          filtered={isFieldFiltered(filters as CrudFilter[], 'mod_status')}
          filterDropdown={({ confirm, clearFilters }) => (
            <SelectFilter
              field="mod_status"
              placeholder={t('base.fields.mod_status')}
              options={Object.values(ModStatus).map((value) => ({
                label: t(`base.mod_status.${value}`),
                value,
              }))}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
        />

        <Table.Column
          dataIndex="search_popularity"
          title={t('user_catalog.common.fields.search_popularity')}
          sorter
          sortOrder={getSortOrder(sorters, 'search_popularity')}
          filtered={isFieldFiltered(
            filters as CrudFilter[],
            'search_popularity'
          )}
          filterDropdown={({ confirm, clearFilters }) => (
            <NumberRangeFilter
              field="search_popularity"
              min={0}
              max={10}
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(v: number) => v ?? 0}
        />

        <Table.Column
          dataIndex="updated_at"
          title={t('base.fields.updated_at')}
          sorter
          sortOrder={getSortOrder(sorters, 'updated_at')}
          filtered={isFieldFiltered(filters as CrudFilter[], 'updated_at')}
          filterDropdown={({ confirm, clearFilters }) => (
            <DateRangeFilter
              field="updated_at"
              filters={filters as CrudFilter[]}
              confirm={confirm}
              clearFilters={clearFilters}
              setFilters={setFilters}
            />
          )}
          render={(value: string) =>
            value ? new Date(value).toLocaleString() : '-'
          }
        />

        <Table.Column
          title={t('actions.column')}
          dataIndex="actions"
          render={(_, record: PublisherListOut) => (
            <Space>
              <EditButton
                hideText
                size="small"
                resource="user_catalog.publishers"
                recordItemId={record.id}
              />
              <DeleteButton
                hideText
                size="small"
                resource="user_catalog.publishers"
                recordItemId={record.id}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
